import {
  Container,
  Flex,
  Heading,
  SkeletonText,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { useEffect } from "react"
import { z } from "zod"

import { OrdersService } from "../../client"
import AddOrder from "../../components/AddOrder"
// import ActionsMenu from "../../components/ActionsMenu"
import Navbar from "../../components/Navbar"

const ordersSearchSchema = z.object({
  page: z.number().catch(1),
})

export const Route = createFileRoute("/_layout/orders")({
  component: Orders,
  validateSearch: (search) => ordersSearchSchema.parse(search),
})

const PER_PAGE = 5

function getOrdersQueryOptions({ page }: { page: number }) {
  return {
    queryFn: () =>
      // OrdersService.readOrders({ skip: (page - 1) * PER_PAGE, limit: PER_PAGE }),
      OrdersService.getOrders(),
    queryKey: ["orders", { page }],
  }
}

function OrdersTable() {
  const queryClient = useQueryClient()
  const { page } = Route.useSearch()

  const {
    data: orders,
    isPending,
    isPlaceholderData,
  } = useQuery({
    ...getOrdersQueryOptions({ page }),
    placeholderData: (prevData) => prevData,
  })

  const hasNextPage = !isPlaceholderData && orders?.data.length === PER_PAGE

  useEffect(() => {
    if (hasNextPage) {
      queryClient.prefetchQuery(getOrdersQueryOptions({ page: page + 1 }))
    }
  }, [page, queryClient, hasNextPage])

  return (
    <>
      <TableContainer>
        <Table size={{ base: "sm", md: "md" }}>
          <Thead>
            <Tr>
              <Th>ID</Th>
              <Th>Name</Th>
              <Th>Details</Th>
            </Tr>
          </Thead>
          {isPending ? (
            <Tbody>
              <Tr>
                {new Array(3).fill(null).map(
                  (
                    _,
                    index, // Updated to 3 columns
                  ) => (
                    <Td key={index}>
                      <SkeletonText noOfLines={1} paddingBlock="16px" />
                    </Td>
                  ),
                )}
              </Tr>
            </Tbody>
          ) : (
            <Tbody>
              {orders?.data.map((order) => (
                <Tr key={order.id} opacity={isPlaceholderData ? 0.5 : 1}>
                  <Td>{order.id}</Td>
                  <Td isTruncated maxWidth="150px">
                    {order.name}
                  </Td>
                  <Td>{JSON.stringify(order.form)}</Td>
                </Tr>
              ))}
            </Tbody>
          )}
        </Table>
      </TableContainer>
      <Flex
        gap={4}
        alignItems="center"
        mt={4}
        direction="row"
        justifyContent="flex-end"
      ></Flex>
    </>
  )
}

function Orders() {
  return (
    <Container maxW="full">
      <Heading size="lg" textAlign={{ base: "center", md: "left" }} pt={12}>
        Order Management
      </Heading>

      <Navbar type={"Order"} addModalAs={AddOrder} />
      <OrdersTable />
    </Container>
  )
}

export default Orders
